import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AdminLayout from './AdminLayout';
import { getComments, updateComment, deleteComment, markAllCommentsAsRead } from '../../services/apiService';

const Comments = () => {
  const [userEmail, setUserEmail] = useState('');
  const [comments, setComments] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(6);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState(''); // 'event' ou 'news'
  const [sortBy, setSortBy] = useState('date'); // 'date', 'author', 'status'
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc' or 'desc'
  const [selectedComment, setSelectedComment] = useState(null); // Pour la popup
  const [commentToDelete, setCommentToDelete] = useState(null); // Pour la popup de suppression
  const [replyToDelete, setReplyToDelete] = useState(null); // Pour la popup de suppression de réponse
  const [commentToReject, setCommentToReject] = useState(null); // Pour la popup de rejet
  const [replyToReject, setReplyToReject] = useState(null); // Pour la popup de rejet de réponse
  const [expandedReplies, setExpandedReplies] = useState({}); // Pour suivre quels commentaires ont leurs réponses développées
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  // Fonction pour charger les commentaires depuis l'API
  const fetchComments = async () => {
    try {
      const commentsData = await getComments({ include_replies: true });
      // Transformer les commentaires de l'API vers le format attendu par le frontend
      // IMPORTANT: Ne garder que les commentaires principaux (parent_id est null ou undefined)
      const transformedComments = commentsData
        .filter(comment => {
          // S'assurer que c'est un commentaire principal (pas une réponse)
          const isMainComment = comment.parent_id === null || comment.parent_id === undefined;
          return isMainComment;
        })
        .map(comment => ({
          id: comment.id,
          author: comment.author || 'Anonyme',
          email: comment.email || '',
          content: comment.content || '',
          article: comment.news_title || comment.event_title || 'Article supprimé',
          type: comment.news_id ? 'news' : (comment.event_id ? 'event' : 'news'),
          status: comment.status || 'Approuvé',
          date: comment.created_at ? comment.created_at.split('T')[0] : (comment.date || ''),
          createdAt: comment.created_at ? comment.created_at.split('T')[0] : (comment.date || ''),
          likes_count: comment.likes_count || 0,
          news_id: comment.news_id || null,
          event_id: comment.event_id || null,
          isReply: false, // Indicateur pour les commentaires principaux
          parent_id: null, // Les commentaires principaux n'ont pas de parent_id
          replies: (comment.replies || [])
            .filter(reply => reply.parent_id === comment.id) // S'assurer qu'elles appartiennent à ce commentaire
            .map(reply => ({
              id: reply.id,
              author: reply.author || 'Anonyme',
              email: reply.email || '',
              content: reply.content || '',
              status: reply.status || 'Approuvé',
              date: reply.created_at ? reply.created_at.split('T')[0] : (reply.date || ''),
              createdAt: reply.created_at ? reply.created_at.split('T')[0] : (reply.date || ''),
              likes_count: reply.likes_count || 0,
              isReply: true, // Indicateur pour les réponses
              parent_id: comment.id // ID du commentaire parent
            }))
        }));
      setComments(transformedComments);
    } catch (err) {
      setComments([]);
    }
  };

  useEffect(() => {
    const isLoggedIn = localStorage.getItem('isLoggedIn');
    const email = localStorage.getItem('userEmail');
    
    if (!isLoggedIn || !email) {
      navigate('/login');
      return;
    }
    
    setUserEmail(email);
    
    // Marquer tous les commentaires non lus comme lus quand l'admin visite la page
    const markCommentsAsRead = async () => {
      try {
        await markAllCommentsAsRead();
      } catch (error) {
      }
    };
    
    // Marquer les commentaires comme lus dès que la page est visitée
    markCommentsAsRead();
    fetchComments();
    
    // Ancien code avec localStorage - maintenant commenté
    /*
    const storedComments = localStorage.getItem('adminComments');
    const defaultComments = [
      {
        id: 1,
        author: 'Jean Dupont',
        email: 'jean@example.com',
        content: 'Excellent article sur les innovations agricoles ! Très informatif et bien documenté.',
        article: 'Innovations technologiques en agriculture',
        type: 'news',
        status: 'Approuvé',
        date: '2024-01-15',
        createdAt: '2024-01-15',
        avatar: 'https://via.placeholder.com/40x40/3b82f6/ffffff?text=JD'
      },
      {
        id: 2,
        author: 'Marie Martin',
        email: 'marie@example.com',
        content: 'Très intéressant, merci pour ces informations. J\'ai appris beaucoup de choses.',
        article: 'Durabilité environnementale',
        type: 'news',
        status: 'En attente',
        date: '2024-01-14',
        createdAt: '2024-01-14',
        avatar: 'https://via.placeholder.com/40x40/10b981/ffffff?text=MM'
      },
      {
        id: 3,
        author: 'Pierre Bernard',
        email: 'pierre@example.com',
        content: 'Superbe événement ! J\'ai hâte d\'y participer. Les présentations promettent d\'être très intéressantes.',
        article: 'MedAgriCycle Consortium Meeting 2024',
        type: 'event',
        status: 'Approuvé',
        date: '2024-01-13',
        createdAt: '2024-01-13',
        avatar: 'https://via.placeholder.com/40x40/8b5cf6/ffffff?text=PB'
      },
      {
        id: 4,
        author: 'Sophie Laurent',
        email: 'sophie@example.com',
        content: 'Bravo pour ce travail ! Ces innovations vont vraiment changer l\'agriculture de demain.',
        article: 'Technologies vertes',
        type: 'news',
        status: 'Approuvé',
        date: '2024-01-12',
        createdAt: '2024-01-12',
        avatar: 'https://via.placeholder.com/40x40/ef4444/ffffff?text=SL'
      },
      {
        id: 5,
        author: 'Luc Moreau',
        email: 'luc@example.com',
        content: 'Atelier très enrichissant ! Les principes de l\'économie circulaire sont parfaitement expliqués.',
        article: 'Circular Economy Workshop',
        type: 'event',
        status: 'En attente',
        date: '2024-01-11',
        createdAt: '2024-01-11',
        avatar: 'https://via.placeholder.com/40x40/f59e0b/ffffff?text=LM'
      },
      {
        id: 6,
        author: 'Emma Dubois',
        email: 'emma@example.com',
        content: 'Conférence exceptionnelle ! Les pratiques présentées sont très pertinentes pour notre secteur.',
        article: 'Sustainable Agriculture Conference',
        type: 'event',
        status: 'Approuvé',
        date: '2024-01-10',
        createdAt: '2024-01-10',
        avatar: 'https://via.placeholder.com/40x40/06b6d4/ffffff?text=ED'
      },
      {
        id: 7,
        author: 'Thomas Leroy',
        email: 'thomas@example.com',
        content: 'Article très bien structuré et facile à comprendre. Merci pour le partage !',
        article: 'Agriculture biologique',
        type: 'news',
        status: 'Rejeté',
        date: '2024-01-09',
        createdAt: '2024-01-09',
        avatar: 'https://via.placeholder.com/40x40/84cc16/ffffff?text=TL'
      },
      {
        id: 8,
        author: 'Camille Rousseau',
        email: 'camille@example.com',
        content: 'Journée Innovation très intéressante ! Les nouvelles technologies agricoles sont impressionnantes.',
        article: 'Technology Innovation Day',
        type: 'event',
        status: 'Approuvé',
        date: '2024-01-08',
        createdAt: '2024-01-08',
        avatar: 'https://via.placeholder.com/40x40/ec4899/ffffff?text=CR'
      },
      {
        id: 9,
        author: 'Antoine Durand',
        email: 'antoine@example.com',
        content: 'Démonstration pratique très instructive. J\'ai beaucoup appris sur les techniques agricoles.',
        article: 'Field Visit and Demonstration',
        type: 'event',
        status: 'Approuvé',
        date: '2024-01-07',
        createdAt: '2024-01-07',
        avatar: 'https://via.placeholder.com/40x40/6366f1/ffffff?text=AD'
      },
      {
        id: 10,
        author: 'Isabelle Moreau',
        email: 'isabelle@example.com',
        content: 'Présentation des résultats très claire et bien structurée. Bravo pour ce travail !',
        article: 'Research Results Presentation',
        type: 'event',
        status: 'En attente',
        date: '2024-01-06',
        createdAt: '2024-01-06',
        avatar: 'https://via.placeholder.com/40x40/14b8a6/ffffff?text=IM'
      },
      {
        id: 11,
        author: 'Marc Lefebvre',
        email: 'marc@example.com',
        content: 'Rencontre avec les parties prenantes très productive. Les discussions étaient enrichissantes.',
        article: 'Stakeholder Engagement Meeting',
        type: 'event',
        status: 'Approuvé',
        date: '2024-01-05',
        createdAt: '2024-01-05',
        avatar: 'https://via.placeholder.com/40x40/f97316/ffffff?text=ML'
      }
    ];
    
    if (storedComments) {
      const parsedComments = JSON.parse(storedComments);
      // Migration : ajouter createdAt et type aux commentaires existants
      const migratedComments = parsedComments.map(comment => ({
        ...comment,
        createdAt: comment.createdAt || comment.date || new Date().toISOString().split('T')[0],
        type: comment.type || 'news' // Par défaut 'news' si non spécifié
      }));
      // S'assurer qu'on a au moins 11 commentaires
      if (migratedComments.length < 11) {
        setComments(defaultComments);
        localStorage.setItem('adminComments', JSON.stringify(defaultComments));
      } else {
        setComments(migratedComments);
        localStorage.setItem('adminComments', JSON.stringify(migratedComments));
      }
    } else {
      setComments(defaultComments);
      localStorage.setItem('adminComments', JSON.stringify(defaultComments));
    }
    */
  }, [location.pathname, navigate]);

  // Gérer le paramètre article et type dans l'URL
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const articleParam = urlParams.get('article');
    const typeParam = urlParams.get('type');
    if (articleParam) {
      setSearchTerm(decodeURIComponent(articleParam));
    }
    if (typeParam) {
      setTypeFilter(typeParam);
    }
  }, [location.search]);


  const handleRejectComment = (id) => {
    const comment = comments.find(c => c.id === id);
    if (comment) {
      setCommentToReject(comment);
    }
  };


  const confirmRejectComment = async () => {
    if (commentToReject) {
      try {
        await updateComment(commentToReject.id, { status: 'Rejeté' });
        // Recharger les commentaires depuis l'API
        await fetchComments();
        setCommentToReject(null);
        setSelectedComment(null); // Fermer aussi la popup de détail si elle est ouverte
      } catch (err) {
        alert('Erreur lors du rejet du commentaire');
      }
    }
  };

  const confirmRejectReply = async () => {
    if (replyToReject) {
      try {
        await updateComment(replyToReject.id, { status: 'Rejeté' });
        await fetchComments();
        // Mettre à jour le commentaire sélectionné pour refléter le changement
        if (selectedComment) {
          const updatedComment = await getComments({ include_replies: true });
          const found = updatedComment.find(c => c.id === selectedComment.id);
          if (found) {
            setSelectedComment({
              ...selectedComment,
              replies: (found.replies || [])
            });
          }
        }
        setReplyToReject(null);
      } catch (err) {
        alert('Erreur lors du rejet de la réponse');
      }
    }
  };

  const handleDeleteComment = (id) => {
    const comment = comments.find(c => c.id === id);
    if (comment) {
      setCommentToDelete(comment);
    }
  };

  const confirmDeleteComment = async () => {
    if (commentToDelete) {
      try {
        await deleteComment(commentToDelete.id);
        // Recharger les commentaires depuis l'API
        await fetchComments();
        setCommentToDelete(null);
        setSelectedComment(null); // Fermer aussi la popup de détail si elle est ouverte
      } catch (err) {
        alert('Erreur lors de la suppression du commentaire');
      }
    }
  };

  const confirmDeleteReply = async () => {
    if (replyToDelete) {
      try {
        await deleteComment(replyToDelete.id);
        await fetchComments();
        // Mettre à jour le commentaire sélectionné pour refléter le changement
        if (selectedComment) {
          const updatedComment = await getComments({ include_replies: true });
          const found = updatedComment.find(c => c.id === selectedComment.id);
          if (found) {
            setSelectedComment({
              ...selectedComment,
              replies: found.replies || []
            });
          }
        }
        setReplyToDelete(null);
      } catch (err) {
        alert('Erreur lors de la suppression de la réponse');
      }
    }
  };

  // Filtrage des commentaires
  const filteredComments = comments.filter(comment => {
    // Filtre par recherche (auteur, email, contenu ou article)
    const matchesSearch = searchTerm === '' || 
      comment.author.toLowerCase().includes(searchTerm.toLowerCase()) ||
      comment.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      comment.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
      comment.article.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Filtre par date
    const matchesDate = dateFilter === '' || 
      comment.date === dateFilter ||
      comment.createdAt === dateFilter;
    
    // Filtre par statut
    const matchesStatus = statusFilter === '' || comment.status === statusFilter;
    
    // Filtre par type
    const matchesType = typeFilter === '' || (comment.type || 'news') === typeFilter;
    
    return matchesSearch && matchesDate && matchesStatus && matchesType;
  });

  // Tri des commentaires filtrés
  const sortedComments = [...filteredComments].sort((a, b) => {
    let comparison = 0;
    
    switch (sortBy) {
      case 'date':
        const dateA = a.createdAt || a.date || '';
        const dateB = b.createdAt || b.date || '';
        comparison = new Date(dateA) - new Date(dateB);
        break;
      case 'author':
        comparison = a.author.localeCompare(b.author, 'fr', { sensitivity: 'base' });
        break;
      case 'status':
        comparison = a.status.localeCompare(b.status, 'fr', { sensitivity: 'base' });
        break;
      default:
        comparison = 0;
    }
    
    return sortOrder === 'asc' ? comparison : -comparison;
  });

  // Réinitialiser la page à 1 lors du changement de filtre ou tri
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, dateFilter, statusFilter, typeFilter, sortBy, sortOrder]);

  // Pagination basée sur les commentaires filtrés et triés
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentComments = sortedComments.slice(startIndex, endIndex);
  const totalPages = Math.ceil(sortedComments.length / itemsPerPage);

  // Obtenir tous les statuts uniques
  const allStatuses = Array.from(new Set(
    comments.map(comment => comment.status).filter(Boolean)
  )).sort();

  // Fonction pour obtenir les initiales
  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // Display helpers (keep stored values like "Approuvé/Rejeté/En attente" intact)
  const getStatusLabel = (status) => {
    if (status === 'Approuvé') return 'Approved';
    if (status === 'Rejeté') return 'Rejected';
    if (status === 'En attente') return 'Pending';
    return status || '';
  };

  const getTypeLabel = (type) => {
    if (type === 'event') return 'Event';
    if (type === 'news') return 'News';
    return type || '';
  };

  return (
    <AdminLayout>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #96ca2d 0%, #7fb800 100%)',
        borderRadius: '16px',
        padding: '24px',
        marginBottom: '24px',
        color: 'white',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: '-20%',
          right: '-10%',
          width: '200px',
          height: '200px',
          background: 'rgba(255, 255, 255, 0.1)',
          borderRadius: '50%',
          zIndex: 1
        }} />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative', zIndex: 2, flexWrap: 'wrap', gap: '20px' }}>
          <div style={{ flex: 1 }}>
            <h1 style={{ margin: 0, fontSize: '28px', fontWeight: 700 }}>Comment Management</h1>
            <p style={{ margin: '8px 0 0 0', opacity: 0.95 }}>Moderate and manage user comments</p>
            {/* Statistiques */}
            <div style={{ 
              display: 'flex', 
              gap: '16px', 
              marginTop: '24px',
              flexWrap: 'wrap'
            }}>
              {/* Total commentaires */}
              <div style={{ 
                display: 'flex', 
                alignItems: 'center',
                gap: '12px',
                background: 'rgba(255, 255, 255, 0.15)',
                backdropFilter: 'blur(10px)',
                padding: '12px 20px',
                borderRadius: '12px',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.25)';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
              >
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '12px',
                  background: 'rgba(255, 255, 255, 0.25)',
            display: 'flex',
            alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '20px',
                  color: 'white'
          }}>
            <i className="fas fa-comments" />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <div style={{ fontSize: '28px', fontWeight: 700, color: 'white', lineHeight: '1.2' }}>
                    {comments.length + comments.reduce((sum, comment) => sum + (comment.replies?.length || 0), 0)}
                  </div>
                  <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: '13px', fontWeight: 500, marginTop: '2px' }}>
                    Total comments
                  </div>
                </div>
              </div>

              {/* Rejected */}
          <div style={{
            display: 'flex',
                alignItems: 'center',
                gap: '12px',
                background: 'rgba(255, 255, 255, 0.15)',
                backdropFilter: 'blur(10px)',
                padding: '12px 20px',
                borderRadius: '12px',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.25)';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
              >
                <div style={{
                  width: window.innerWidth <= 768 ? '40px' : '48px',
                  height: window.innerWidth <= 768 ? '40px' : '48px',
                  borderRadius: '12px',
                  background: 'rgba(255, 255, 255, 0.25)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: window.innerWidth <= 768 ? '18px' : '20px',
                  color: 'white',
                  flexShrink: 0
                }}>
                  <i className="fas fa-times-circle" />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: window.innerWidth <= 768 ? '22px' : '28px', fontWeight: 700, color: 'white', lineHeight: '1.2' }}>
                    {comments.filter(c => c.status === 'Rejeté').length + comments.reduce((sum, comment) => sum + (comment.replies?.filter(r => r.status === 'Rejeté').length || 0), 0)}
                  </div>
                  <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: window.innerWidth <= 768 ? '11px' : '13px', fontWeight: 500, marginTop: '2px' }}>
                    Rejected
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filtres */}
      <div style={{
        background: 'white',
        borderRadius: '16px',
        padding: '20px',
        marginBottom: '24px',
        border: '1px solid rgba(150, 202, 45, 0.1)',
        display: 'flex',
        gap: '16px',
        flexWrap: 'wrap',
        alignItems: 'center'
      }}>
        {/* Recherche */}
        <div style={{ flex: '1', minWidth: '250px' }}>
          <div style={{ position: 'relative' }}>
            <i className="fas fa-search" style={{
              position: 'absolute',
              left: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#9ca3af',
              fontSize: '14px'
            }} />
            <input
              type="text"
              placeholder="Search by author, email, content, or article..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px 10px 38px',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '14px',
                transition: 'all 0.2s'
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = '#96ca2d';
                e.currentTarget.style.boxShadow = '0 0 0 3px rgba(150, 202, 45, 0.1)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = '#e5e7eb';
                e.currentTarget.style.boxShadow = 'none';
              }}
            />
          </div>
        </div>

        {/* Filtre par date */}
        <div style={{ minWidth: '180px' }}>
          <div style={{ position: 'relative' }}>
            <i className="fas fa-calendar" style={{
              position: 'absolute',
              left: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#9ca3af',
              fontSize: '14px'
            }} />
            <input
              type="date"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px 10px 38px',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '14px',
                transition: 'all 0.2s'
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = '#96ca2d';
                e.currentTarget.style.boxShadow = '0 0 0 3px rgba(150, 202, 45, 0.1)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = '#e5e7eb';
                e.currentTarget.style.boxShadow = 'none';
              }}
            />
          </div>
        </div>

        {/* Filtre par statut */}
        <div style={{ minWidth: '180px' }}>
          <div style={{ position: 'relative' }}>
            <i className="fas fa-filter" style={{
              position: 'absolute',
              left: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#9ca3af',
              fontSize: '14px',
              zIndex: 1
            }} />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px 10px 38px',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '14px',
                backgroundColor: 'white',
                cursor: 'pointer',
                transition: 'all 0.2s',
                appearance: 'none',
                backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' viewBox=\'0 0 12 12\'%3E%3Cpath fill=\'%23666\' d=\'M6 9L1 4h10z\'/%3E%3C/svg%3E")',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 12px center',
                paddingRight: '36px'
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = '#96ca2d';
                e.currentTarget.style.boxShadow = '0 0 0 3px rgba(150, 202, 45, 0.1)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = '#e5e7eb';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <option value="">All statuses</option>
              {allStatuses.map(status => (
                <option key={status} value={status}>{getStatusLabel(status)}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Filtre par type */}
        <div style={{ minWidth: '180px' }}>
          <div style={{ position: 'relative' }}>
            <i className="fas fa-tag" style={{
              position: 'absolute',
              left: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#9ca3af',
              fontSize: '14px',
              zIndex: 1
            }} />
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px 10px 38px',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '14px',
                backgroundColor: 'white',
                cursor: 'pointer',
                transition: 'all 0.2s',
                appearance: 'none',
                backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' viewBox=\'0 0 12 12\'%3E%3Cpath fill=\'%23666\' d=\'M6 9L1 4h10z\'/%3E%3C/svg%3E")',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 12px center',
                paddingRight: '36px'
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = '#96ca2d';
                e.currentTarget.style.boxShadow = '0 0 0 3px rgba(150, 202, 45, 0.1)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = '#e5e7eb';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <option value="">All types</option>
              <option value="event">Event</option>
              <option value="news">News</option>
            </select>
          </div>
        </div>

        {/* Tri */}
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            style={{
              padding: '10px 12px',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              fontSize: '14px',
              backgroundColor: 'white',
              cursor: 'pointer',
              transition: 'all 0.2s',
              appearance: 'none',
              backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' viewBox=\'0 0 12 12\'%3E%3Cpath fill=\'%23666\' d=\'M6 9L1 4h10z\'/%3E%3C/svg%3E")',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 12px center',
              paddingRight: '36px'
            }}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = '#96ca2d';
              e.currentTarget.style.boxShadow = '0 0 0 3px rgba(150, 202, 45, 0.1)';
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = '#e5e7eb';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <option value="date">Sort by date</option>
            <option value="author">Sort by author</option>
            <option value="status">Sort by status</option>
          </select>
          <select
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
            style={{
              padding: '10px 12px',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              fontSize: '14px',
              backgroundColor: 'white',
              cursor: 'pointer',
              transition: 'all 0.2s',
              appearance: 'none',
              backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' viewBox=\'0 0 12 12\'%3E%3Cpath fill=\'%23666\' d=\'M6 9L1 4h10z\'/%3E%3C/svg%3E")',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 12px center',
              paddingRight: '36px'
            }}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = '#96ca2d';
              e.currentTarget.style.boxShadow = '0 0 0 3px rgba(150, 202, 45, 0.1)';
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = '#e5e7eb';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <option value="desc">Descending</option>
            <option value="asc">Ascending</option>
          </select>
        </div>
      </div>

      {/* Bouton réinitialiser */}
      {(searchTerm || dateFilter || statusFilter || typeFilter) && (
        <button
          onClick={() => {
            setSearchTerm('');
            setDateFilter('');
            setStatusFilter('');
            setTypeFilter('');
          }}
          style={{
            padding: '10px 20px',
            background: '#f3f4f6',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            color: '#6b7280',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: 500,
            transition: 'all 0.2s',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '24px'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = '#e5e7eb';
            e.currentTarget.style.borderColor = '#d1d5db';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = '#f3f4f6';
            e.currentTarget.style.borderColor = '#e5e7eb';
          }}
        >
          <i className="fas fa-times" style={{ fontSize: '12px' }} />
          Reset
        </button>
      )}

      {/* Comments List */}
      <div style={{
        background: 'white',
        borderRadius: '16px',
        padding: '24px',
        border: '1px solid rgba(150, 202, 45, 0.1)'
      }}>
        {sortedComments.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#6c757d' }}>
            <p>
              {(searchTerm || dateFilter || statusFilter || typeFilter) 
                ? 'No comments match the selected filters.'
                : 'No comments yet.'}
            </p>
            {(searchTerm || dateFilter || statusFilter || typeFilter) && (
              <button
                onClick={() => {
                  setSearchTerm('');
                  setDateFilter('');
                  setStatusFilter('');
                  setTypeFilter('');
                }}
                style={{
                  padding: '12px 24px',
                  background: '#96ca2d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: 600,
                  marginTop: '16px'
                }}
              >
                Reset filters
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Results indicator */}
            {(searchTerm || dateFilter || statusFilter || typeFilter) && (
              <div style={{
                marginBottom: '20px',
                padding: '12px 16px',
                background: '#f0fdf4',
                border: '1px solid #86efac',
                borderRadius: '8px',
                color: '#166534',
                fontSize: '14px',
          display: 'flex',
          alignItems: 'center',
                gap: '8px'
              }}>
                <i className="fas fa-info-circle" style={{ fontSize: '16px' }} />
                <span>
                  {sortedComments.length} {sortedComments.length === 1 ? 'result found' : 'results found'}
                  {(searchTerm || dateFilter || statusFilter || typeFilter) && ' with filters applied'}
                </span>
              </div>
            )}
            <div style={{
              display: 'grid',
              gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : 'repeat(auto-fill, minmax(300px, 1fr))',
              gap: window.innerWidth <= 768 ? '16px' : '20px'
            }}>
              {currentComments.map(comment => (
                <div key={comment.id} style={{
                  background: 'white',
                  border: '2px solid #e5e7eb',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#96ca2d';
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 8px 20px rgba(150, 202, 45, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.08)';
                }}
                >
              <div style={{
                    position: 'absolute',
                    top: '0px',
                    left: '0px',
                    right: '0px',
                    height: '3px',
                    background: 'linear-gradient(135deg, rgb(150, 202, 45) 0%, rgb(127, 184, 0) 100%)',
                    transform: 'translateY(0px)',
                    borderColor: 'rgba(150, 202, 45, 0.1)',
                    zIndex: 2
                  }} />
                  
                  <div style={{ 
                    padding: '16px',
                display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    minHeight: '200px',
                    height: '100%'
                  }}>
                    {/* Auteur - hauteur fixe */}
                    <div style={{ height: '44px', marginBottom: '16px', display: 'flex', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                          <h2 style={{ 
                            margin: 0, 
                            fontSize: '14px', 
                            fontWeight: 600, 
                            color: '#1f2937',
                            lineHeight: '1.4',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            flex: 1
                          }}>{comment.author}</h2>
                          <span style={{
                            fontSize: '10px',
                            background: '#e0e7ff',
                            color: '#3730a3',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            fontWeight: 600,
                            whiteSpace: 'nowrap'
                          }}>
                            <i className="fas fa-comment" style={{ marginRight: '4px', fontSize: '9px' }} />
                            Commentaire
                          </span>
                        </div>
                        <p style={{
                          margin: 0,
                          fontSize: '12px',
                          color: '#6b7280'
                        }}>{comment.email}</p>
                      </div>
                    </div>
                    
                    {/* Contenu - hauteur fixe */}
                    <div style={{ height: '40px', marginBottom: '8px' }}>
                      <p style={{ 
                        margin: 0, 
                        color: '#6b7280', 
                        fontSize: '14px', 
                        lineHeight: '1.5', 
                        display: '-webkit-box', 
                        WebkitLineClamp: 2, 
                        WebkitBoxOrient: 'vertical', 
                        overflow: 'hidden' 
                      }}>{comment.content}</p>
                    </div>
                    
                    {/* Article - hauteur fixe */}
                  <div style={{
                      height: '32px',
                      marginBottom: '12px',
                      display: 'flex', 
                      alignItems: 'center'
                    }}>
                      <span style={{
                        fontSize: '12px',
                        color: '#9ca3af',
                    display: 'flex',
                    alignItems: 'center',
                        gap: '6px'
                      }}>
                        <i className="fas fa-file-alt" style={{ fontSize: '11px' }} />
                        <span style={{
                          display: '-webkit-box',
                          WebkitLineClamp: 1,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden'
                        }}>{comment.article}</span>
                      </span>
                    </div>
                    
        {/* Métadonnées - hauteur fixe */}
        <div style={{ 
          minHeight: '26px', 
          display: 'flex', 
        gap: '12px',
          fontSize: '12px', 
          color: '#9ca3af', 
          marginBottom: '12px',
          alignItems: 'center',
          flexWrap: 'wrap'
        }}>
          <span><i className="fas fa-calendar" style={{ marginRight: '4px' }} />{comment.date}</span>
          <span style={{
            background: comment.type === 'event' ? '#dbeafe' : '#fef3c7',
            color: comment.type === 'event' ? '#1e40af' : '#92400e',
            padding: '2px 8px',
            borderRadius: '4px',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}>
            <i className={comment.type === 'event' ? 'fas fa-calendar-alt' : 'fas fa-newspaper'} style={{ fontSize: '10px' }} />
            {getTypeLabel(comment.type)}
          </span>
          {/* Nombre de j'aime */}
          <span style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            background: '#fef3c7',
            color: '#d97706',
            padding: '2px 8px',
            borderRadius: '4px',
            fontWeight: 600
          }}>
            <i className="fas fa-thumbs-up" style={{ fontSize: '10px' }} />
            {comment.likes_count || 0}
          </span>
        {/* Only show the "Rejected" badge if status is "Rejeté" */}
        {comment.status === 'Rejeté' && (
          <span style={{
            background: '#fef2f2',
            color: '#991b1b',
            padding: '2px 8px',
            borderRadius: '4px',
            fontWeight: 600
          }}>{getStatusLabel(comment.status)}</span>
        )}
          {/* Icône pour développer/réduire les réponses */}
          {comment.replies && comment.replies.length > 0 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setExpandedReplies(prev => ({
                  ...prev,
                  [comment.id]: !prev[comment.id]
                }));
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                padding: '4px 10px',
                background: expandedReplies[comment.id] 
                  ? 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' 
                  : 'linear-gradient(135deg, #a78bfa 0%, #c084fc 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '11px',
                fontWeight: 700,
                transition: 'all 0.3s ease',
                boxShadow: expandedReplies[comment.id] 
                  ? '0 2px 8px rgba(99, 102, 241, 0.4)' 
                  : '0 2px 6px rgba(167, 139, 250, 0.3)'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)';
                e.target.style.boxShadow = '0 4px 12px rgba(99, 102, 241, 0.5)';
                e.target.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                if (!expandedReplies[comment.id]) {
                  e.target.style.background = 'linear-gradient(135deg, #a78bfa 0%, #c084fc 100%)';
                  e.target.style.boxShadow = '0 2px 6px rgba(167, 139, 250, 0.3)';
                }
                e.target.style.transform = 'translateY(0)';
              }}
            >
              <i className={expandedReplies[comment.id] ? 'fas fa-minus' : 'fas fa-plus'} style={{ fontSize: '9px' }} />
              <span>{comment.replies.length} {comment.replies.length === 1 ? 'reply' : 'replies'}</span>
            </button>
          )}
      </div>
      
        {/* Réponses */}
                    {comment.replies && comment.replies.length > 0 && expandedReplies[comment.id] && (
                      <div style={{
                        marginBottom: '12px',
                        padding: '12px',
                        background: '#f0f9ff',
                        borderRadius: '8px',
                        border: '2px solid #bfdbfe',
                        flexShrink: 0
                      }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          marginBottom: '8px',
                          fontSize: '12px',
                          fontWeight: 600,
                          color: '#1e40af'
                        }}>
                          <i className="fas fa-reply" style={{ fontSize: '11px' }} />
                          <span>{comment.replies.length} {comment.replies.length === 1 ? 'reply' : 'replies'}</span>
                        </div>
                        {comment.replies.map((reply, replyIndex) => (
                          <div key={reply.id} style={{
                            marginBottom: replyIndex < comment.replies.length - 1 ? '8px' : '0',
                            paddingBottom: replyIndex < comment.replies.length - 1 ? '8px' : '0',
                            borderBottom: replyIndex < comment.replies.length - 1 ? '1px solid #bfdbfe' : 'none',
                            paddingLeft: '8px',
                            borderLeft: '3px solid #3b82f6'
                          }}>
                            <div style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'flex-start',
                              marginBottom: '4px'
                            }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <span style={{
                                  fontSize: '11px',
                                  fontWeight: 600,
                                  color: '#1f2937'
                                }}>{reply.author}</span>
                                <span style={{
                                  fontSize: '9px',
                                  background: '#dbeafe',
                                  color: '#1e40af',
                                  padding: '2px 5px',
                                  borderRadius: '3px',
                                  fontWeight: 600,
                                  whiteSpace: 'nowrap'
                                }}>
                                  <i className="fas fa-reply" style={{ marginRight: '3px', fontSize: '8px' }} />
                                  Reply
                                </span>
                              </div>
                              {/* Only show the "Rejected" badge if status is "Rejeté" */}
                              {reply.status === 'Rejeté' && (
                                <span style={{
                                  fontSize: '10px',
                                  background: '#fef2f2',
                                  color: '#991b1b',
                                  padding: '2px 6px',
                                  borderRadius: '4px',
                                  fontWeight: 600
                                }}>{getStatusLabel(reply.status)}</span>
                              )}
                            </div>
                            <p style={{
                              margin: '0 0 4px 0',
                              fontSize: '11px',
                              color: '#6b7280',
                              lineHeight: '1.4',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden'
                            }}>{reply.content}</p>
                            {/* Nombre de j'aime pour les réponses */}
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              fontSize: '10px',
                              color: '#d97706',
                              marginTop: '4px'
                            }}>
                              <i className="fas fa-thumbs-up" style={{ fontSize: '9px' }} />
                              <span style={{ fontWeight: 600 }}>{reply.likes_count || 0}</span>
                            </div>
                          </div>
                            ))}
                      </div>
                    )}
              
                    {/* Action buttons */}
              <div style={{
                display: 'flex',
                gap: '8px',
                      marginTop: 'auto',
                      flexWrap: 'wrap',
                      paddingTop: '12px'
              }}>
                <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedComment(comment);
                          }}
                  style={{
                            flex: 1,
                            padding: '8px 12px',
                            background: '#f3f4f6',
                            color: '#6b7280',
                    border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '12px',
                            fontWeight: 600,
                            transition: 'all 0.2s',
                            minWidth: '80px'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.background = '#e5e7eb';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.background = '#f3f4f6';
                          }}
                        >
                          <i className="fas fa-eye" style={{ marginRight: '4px' }} />
                          View
                        </button>
                {comment.status === 'Approuvé' && (
                  <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRejectComment(comment.id);
                            }}
                      style={{
                              flex: 1,
                              padding: '8px 12px',
                              background: '#f59e0b',
                              color: 'white',
                        border: 'none',
                              borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '12px',
                              fontWeight: 600,
                              transition: 'all 0.2s',
                              minWidth: '100px'
                      }}
                      onMouseEnter={(e) => {
                              e.target.style.background = '#d97706';
                      }}
                      onMouseLeave={(e) => {
                              e.target.style.background = '#f59e0b';
                      }}
                    >
                      <i className="fas fa-times" style={{ marginRight: '4px' }} />
                      Reject
                    </button>
                )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteComment(comment.id);
                        }}
                        style={{
                    padding: '8px 12px',
                          background: 'transparent',
                          color: '#ef4444',
                          border: '1px solid #ef4444',
                          borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '12px',
                          fontWeight: 600,
                          transition: 'all 0.2s',
                          minWidth: '100px'
                  }}
                  onMouseEnter={(e) => {
                          e.target.style.background = '#ef4444';
                          e.target.style.color = 'white';
                  }}
                  onMouseLeave={(e) => {
                          e.target.style.background = 'transparent';
                          e.target.style.color = '#ef4444';
                  }}
                >
                        <i className="fas fa-trash" style={{ marginRight: '4px' }} />
                        Delete
                </button>
                    </div>
              </div>
            </div>
          ))}
        </div>

            {/* Pagination */}
            {sortedComments.length > itemsPerPage && (
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 0,
                marginTop: '30px',
                flexWrap: 'wrap'
              }}>
                <div style={{ display: 'inline-flex', gap: 0 }}>
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    style={{
                      padding: '8px 10px',
                      minWidth: 36,
                      borderRadius: '6px 0 0 6px',
                      border: '1px solid #96ca2d',
                      background: currentPage === 1 ? '#f2f2f2' : '#96ca2d',
                      color: currentPage === 1 ? '#888' : '#fff',
                      cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                      margin: 0,
                      borderRightWidth: 0
                    }}
                  >&lt;</button>
                  {(() => {
                    const buttons = [];
                    const renderBtn = (i, isFirst = false) => (
                      <button
                        key={i}
                        onClick={() => setCurrentPage(i)}
                        style={{
                          padding: '8px 10px',
                          minWidth: 36,
                          borderRadius: 0,
                          border: `1px solid ${currentPage === i ? '#96ca2d' : '#ddd'}`,
                          background: currentPage === i ? '#96ca2d' : '#fff',
                          color: currentPage === i ? '#fff' : '#333',
                          cursor: 'pointer',
                          margin: 0,
                          borderLeftWidth: isFirst ? '1px' : 0
                        }}
                      >
                        {i}
                      </button>
                    );

                    const shown = Math.min(totalPages, 3);
                    for (let i = 1; i <= shown; i++) {
                      buttons.push(renderBtn(i, i === 1));
                    }
                    if (totalPages > 3) {
                      buttons.push(
                        <span key="ell" style={{ padding: '8px 6px', border: '1px solid #ddd', borderLeftWidth: 0 }}>...</span>
                      );
                    }
                    return buttons;
                  })()}
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    style={{
                      padding: '8px 10px',
                      minWidth: 36,
                      borderRadius: '0 6px 6px 0',
                      border: '1px solid #96ca2d',
                      background: currentPage === totalPages ? '#f2f2f2' : '#96ca2d',
                      color: currentPage === totalPages ? '#888' : '#fff',
                      cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                      margin: 0,
                      borderLeftWidth: 0
                    }}
                  >&gt;</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Popup pour voir un commentaire */}
      {selectedComment && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            padding: window.innerWidth <= 768 ? '10px' : '20px',
            paddingTop: window.innerWidth <= 768 ? '74px' : '20px',
            overflowY: 'auto'
          }}
        >
          <div
            style={{
              background: 'white',
              borderRadius: window.innerWidth <= 768 ? '12px' : '16px',
              maxWidth: window.innerWidth <= 768 ? '100%' : '600px',
              width: '100%',
              maxHeight: window.innerWidth <= 768 ? '95vh' : '90vh',
              overflowY: 'auto',
              position: 'relative',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Barre de gradient */}
            <div style={{
              position: 'absolute',
              top: '0px',
              left: '0px',
              right: '0px',
              height: '4px',
              background: 'linear-gradient(135deg, rgb(150, 202, 45) 0%, rgb(127, 184, 0) 100%)',
              borderRadius: '16px 16px 0 0'
            }} />
            
            {/* Bouton fermer */}
            <button
              onClick={() => setSelectedComment(null)}
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                background: '#f3f4f6',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#6b7280',
                fontSize: '18px',
                transition: 'all 0.2s',
                zIndex: 10
              }}
              onMouseEnter={(e) => {
                e.target.style.background = '#ef4444';
                e.target.style.color = 'white';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = '#f3f4f6';
                e.target.style.color = '#6b7280';
              }}
            >
              <i className="fas fa-times" />
            </button>

            {/* Contenu de la popup */}
            <div style={{ padding: '24px' }}>
              {/* En-tête avec info auteur */}
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                marginBottom: '24px',
                paddingBottom: '24px',
                borderBottom: '1px solid #e5e7eb'
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', flexWrap: 'wrap' }}>
                    <h2 style={{
                      margin: 0,
                      fontSize: window.innerWidth <= 768 ? '18px' : '20px',
                      fontWeight: 700,
                      color: '#1f2937'
                    }}>
                      {selectedComment.author}
                    </h2>
                    <span style={{
                      fontSize: '11px',
                      background: '#e0e7ff',
                      color: '#3730a3',
                      padding: '4px 10px',
                      borderRadius: '6px',
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}>
                      <i className="fas fa-comment" style={{ fontSize: '10px' }} />
                      Commentaire
                    </span>
                  </div>
                  <p style={{
                    margin: 0,
                    fontSize: window.innerWidth <= 768 ? '13px' : '14px',
                    color: '#6b7280',
                    marginBottom: '12px'
                  }}>
                    {selectedComment.email}
                  </p>
                  <div style={{
                    display: 'flex',
                    gap: '8px',
                    flexWrap: 'wrap'
                  }}>
                  <span style={{
                    background: selectedComment.type === 'event' ? '#dbeafe' : '#fef3c7',
                    color: selectedComment.type === 'event' ? '#1e40af' : '#92400e',
                    padding: '4px 12px',
                    borderRadius: '6px',
                    fontSize: '12px',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    <i className={selectedComment.type === 'event' ? 'fas fa-calendar-alt' : 'fas fa-newspaper'} />
                    {getTypeLabel(selectedComment.type)}
                  </span>
                  {/* Nombre de j'aime */}
                  <span style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    background: '#fef3c7',
                    color: '#d97706',
                    padding: '4px 12px',
                    borderRadius: '6px',
                    fontSize: '12px',
                    fontWeight: 600
                  }}>
                    <i className="fas fa-thumbs-up" style={{ fontSize: '11px' }} />
                    {selectedComment.likes_count || 0}
                  </span>
                  {/* Only show the "Rejected" badge if status is "Rejeté" */}
                  {selectedComment.status === 'Rejeté' && (
                    <span style={{
                      background: '#fef2f2',
                      color: '#991b1b',
                      padding: '4px 12px',
                      borderRadius: '6px',
                      fontSize: '12px',
                      fontWeight: 600
                    }}>
                      {getStatusLabel(selectedComment.status)}
                    </span>
                  )}
                  </div>
                </div>
              </div>

              {/* Associated item */}
              <div style={{
                marginBottom: '20px',
                padding: '12px 16px',
                background: '#f9fafb',
                borderRadius: '8px',
                border: '1px solid #e5e7eb'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '4px'
                }}>
                  <i className="fas fa-file-alt" style={{ color: '#96ca2d', fontSize: '14px' }} />
                  <span style={{
                    fontSize: '12px',
                    fontWeight: 600,
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Associated item
                  </span>
                </div>
                <p style={{
                  margin: 0,
                  fontSize: '15px',
                  fontWeight: 600,
                  color: '#1f2937'
                }}>
                  {selectedComment.article}
                </p>
              </div>

              {/* Comment content */}
              <div style={{
                marginBottom: '20px'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '12px'
                }}>
                  <i className="fas fa-comment" style={{ color: '#96ca2d', fontSize: '14px' }} />
                  <span style={{
                    fontSize: '12px',
                    fontWeight: 600,
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Comment
                  </span>
                </div>
                <p style={{
                  margin: 0,
                  fontSize: '15px',
                  lineHeight: '1.6',
                  color: '#374151',
                  whiteSpace: 'pre-wrap'
                }}>
                  {selectedComment.content}
                </p>
              </div>

              {/* Réponses */}
              {selectedComment.replies && selectedComment.replies.length > 0 && (
                <div style={{
                  marginBottom: '20px',
                  padding: '16px',
                  background: '#f0f9ff',
                  borderRadius: '8px',
                  border: '2px solid #bfdbfe'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '16px',
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#1e40af'
                  }}>
                    <i className="fas fa-reply" style={{ color: '#3b82f6', fontSize: '14px' }} />
                    <span>{selectedComment.replies.length} {selectedComment.replies.length === 1 ? 'Reply' : 'Replies'}</span>
                  </div>
                  {selectedComment.replies.map((reply, index) => (
                    <div key={reply.id} style={{
                      marginBottom: index < selectedComment.replies.length - 1 ? '16px' : '0',
                      paddingBottom: index < selectedComment.replies.length - 1 ? '16px' : '0',
                      borderBottom: index < selectedComment.replies.length - 1 ? '1px solid #bfdbfe' : 'none',
                      paddingLeft: '12px',
                      borderLeft: '3px solid #3b82f6'
                    }}>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        marginBottom: '8px'
                      }}>
                        <div>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            marginBottom: '4px',
                            flexWrap: 'wrap'
                          }}>
                            <div style={{
                              fontSize: '14px',
                              fontWeight: 600,
                              color: '#1f2937'
                            }}>{reply.author}</div>
                            <span style={{
                              fontSize: '10px',
                              background: '#dbeafe',
                              color: '#1e40af',
                              padding: '3px 8px',
                              borderRadius: '4px',
                              fontWeight: 600,
                              whiteSpace: 'nowrap',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '5px'
                            }}>
                              <i className="fas fa-reply" style={{ fontSize: '9px' }} />
                              Reply
                            </span>
                          </div>
                          <div style={{
                            fontSize: '12px',
                            color: '#6b7280'
                          }}>{reply.email}</div>
                        </div>
                      {/* Only show the "Rejected" badge if status is "Rejeté" */}
                      {reply.status === 'Rejeté' && (
                        <span style={{
                          fontSize: '11px',
                          background: '#fef2f2',
                          color: '#991b1b',
                          padding: '4px 10px',
                          borderRadius: '6px',
                          fontWeight: 600
                        }}>{getStatusLabel(reply.status)}</span>
                      )}
                      </div>
                      <p style={{
                        margin: '0 0 8px 0',
                        fontSize: '14px',
                        lineHeight: '1.6',
                        color: '#374151',
                        whiteSpace: 'pre-wrap'
                      }}>{reply.content}</p>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        fontSize: '12px',
                        color: '#9ca3af',
                        marginBottom: '8px',
                        flexWrap: 'wrap'
                      }}>
                        <span><i className="fas fa-calendar" style={{ marginRight: '4px' }} />{reply.date}</span>
                        {/* Nombre de j'aime pour les réponses */}
                        <span style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          color: '#d97706',
                          fontWeight: 600
                        }}>
                          <i className="fas fa-thumbs-up" style={{ fontSize: '11px' }} />
                          {reply.likes_count || 0}
                        </span>
                      </div>
                      <div style={{
                        display: 'flex',
                        gap: '8px',
                        marginTop: '8px',
                        flexWrap: 'wrap'
                      }}>
                        {reply.status === 'Approuvé' && (
                          <button
                            onClick={() => {
                              setReplyToReject({
                                id: reply.id,
                                author: reply.author,
                                content: reply.content,
                                article: selectedComment.article
                              });
                            }}
                            style={{
                              padding: '6px 12px',
                              background: '#f59e0b',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '12px',
                              fontWeight: 600,
                              transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.background = '#d97706';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.background = '#f59e0b';
                            }}
                          >
                            <i className="fas fa-times" style={{ marginRight: '4px' }} />
                            Reject
                          </button>
                        )}
                        <button
                          onClick={() => {
                            setReplyToDelete({
                              id: reply.id,
                              author: reply.author,
                              content: reply.content,
                              article: selectedComment.article
                            });
                          }}
                          style={{
                            padding: '6px 12px',
                            background: 'transparent',
                            color: '#ef4444',
                            border: '1px solid #ef4444',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '12px',
                            fontWeight: 600,
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.background = '#ef4444';
                            e.target.style.color = 'white';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.background = 'transparent';
                            e.target.style.color = '#ef4444';
                          }}
                        >
                          <i className="fas fa-trash" style={{ marginRight: '4px' }} />
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Métadonnées */}
              <div style={{
                display: 'flex',
                gap: '16px',
                paddingTop: '20px',
                borderTop: '1px solid #e5e7eb',
                flexWrap: 'wrap'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '14px',
                  color: '#6b7280'
                }}>
                  <i className="fas fa-calendar" style={{ color: '#96ca2d' }} />
                  <span><strong>Date:</strong> {selectedComment.date}</span>
                </div>
                {selectedComment.createdAt && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontSize: '14px',
                    color: '#6b7280'
                  }}>
                    <i className="fas fa-calendar-plus" style={{ color: '#96ca2d' }} />
                    <span><strong>Créé:</strong> {selectedComment.createdAt}</span>
                  </div>
                )}
              </div>

              {/* Boutons d'action */}
              <div style={{
                display: 'flex',
                gap: '12px',
                marginTop: '24px',
                paddingTop: '20px',
                borderTop: '1px solid #e5e7eb',
                flexWrap: 'wrap'
              }}>
                {selectedComment.status === 'Approuvé' && (
                  <button
                    onClick={() => {
                      handleRejectComment(selectedComment.id);
                    }}
                    style={{
                      flex: 1,
                      padding: '12px 20px',
                      background: '#f59e0b',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: 600,
                      transition: 'all 0.2s',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      minWidth: '150px'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = '#d97706';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = '#f59e0b';
                    }}
                  >
                    <i className="fas fa-times" />
                    Reject
                  </button>
                )}
                <button
                  onClick={() => {
                    handleDeleteComment(selectedComment.id);
                    setSelectedComment(null);
                  }}
                  style={{
                    padding: '12px 20px',
                    background: 'transparent',
                    color: '#ef4444',
                    border: '2px solid #ef4444',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 600,
                    transition: 'all 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    minWidth: '150px'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = '#ef4444';
                    e.target.style.color = 'white';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = 'transparent';
                    e.target.style.color = '#ef4444';
                  }}
                >
                  <i className="fas fa-trash" />
                  Delete
                </button>
                <button
                  onClick={() => {
                    setSelectedComment(null);
                  }}
                  style={{
                    padding: '12px 20px',
                    background: '#6b7280',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 600,
                    transition: 'all 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    minWidth: '150px'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = '#4b5563';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = '#6b7280';
                  }}
                >
                  <i className="fas fa-times" style={{ marginRight: '4px' }} />
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Popup de confirmation de suppression de réponse */}
      {replyToDelete && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10001,
            padding: window.innerWidth <= 768 ? '10px' : '20px',
            paddingTop: window.innerWidth <= 768 ? '74px' : '20px',
            overflowY: 'auto'
          }}
        >
          <div
            style={{
              background: 'white',
              borderRadius: window.innerWidth <= 768 ? '12px' : '16px',
              maxWidth: window.innerWidth <= 768 ? '100%' : '450px',
              width: '100%',
              position: 'relative',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Barre de gradient */}
            <div style={{
              position: 'absolute',
              top: '0px',
              left: '0px',
              right: '0px',
              height: '4px',
              background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              borderRadius: window.innerWidth <= 768 ? '12px 12px 0 0' : '16px 16px 0 0'
            }} />
            
            {/* Contenu de la popup */}
            <div style={{ padding: window.innerWidth <= 768 ? '16px' : '24px' }}>
              {/* Icône d'avertissement */}
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                marginBottom: window.innerWidth <= 768 ? '16px' : '20px'
              }}>
                <div style={{
                  width: window.innerWidth <= 768 ? '56px' : '64px',
                  height: window.innerWidth <= 768 ? '56px' : '64px',
                  borderRadius: '50%',
                  background: '#fef2f2',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '3px solid #fee2e2'
                }}>
                  <i className="fas fa-exclamation-triangle" style={{
                    fontSize: window.innerWidth <= 768 ? '28px' : '32px',
                    color: '#ef4444'
                  }} />
                </div>
              </div>

              {/* Titre */}
              <h2 style={{
                margin: 0,
                fontSize: window.innerWidth <= 768 ? '18px' : '20px',
                fontWeight: 700,
                color: '#1f2937',
                textAlign: 'center',
                marginBottom: '12px'
              }}>
                Delete Reply
              </h2>

              {/* Message */}
              <p style={{
                margin: 0,
                fontSize: window.innerWidth <= 768 ? '13px' : '14px',
                color: '#6b7280',
                textAlign: 'center',
                lineHeight: '1.6',
                marginBottom: window.innerWidth <= 768 ? '20px' : '24px'
              }}>
                Are you sure you want to delete the reply from <strong>{replyToDelete.author}</strong> on <strong>"{replyToDelete.article}"</strong>? This action cannot be undone.
              </p>

              {/* Boutons */}
              <div style={{
                display: 'flex',
                gap: window.innerWidth <= 768 ? '8px' : '12px',
                flexDirection: window.innerWidth <= 768 ? 'column' : 'row'
              }}>
                <button
                  onClick={() => setReplyToDelete(null)}
                  style={{
                    flex: 1,
                    padding: '12px 20px',
                    background: '#f3f4f6',
                    color: '#374151',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 600,
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = '#e5e7eb';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = '#f3f4f6';
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteReply}
                  style={{
                    flex: 1,
                    padding: '12px 20px',
                    background: '#ef4444',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 600,
                    transition: 'all 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = '#dc2626';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = '#ef4444';
                  }}
                >
                  <i className="fas fa-trash" style={{ fontSize: '14px' }} />
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Popup de confirmation de suppression */}
      {commentToDelete && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10001,
            padding: window.innerWidth <= 768 ? '10px' : '20px',
            paddingTop: window.innerWidth <= 768 ? '74px' : '20px',
            overflowY: 'auto'
          }}
        >
          <div
            style={{
              background: 'white',
              borderRadius: window.innerWidth <= 768 ? '12px' : '16px',
              maxWidth: window.innerWidth <= 768 ? '100%' : '450px',
              width: '100%',
              position: 'relative',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Barre de gradient */}
            <div style={{
              position: 'absolute',
              top: '0px',
              left: '0px',
              right: '0px',
              height: '4px',
              background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              borderRadius: window.innerWidth <= 768 ? '12px 12px 0 0' : '16px 16px 0 0'
            }} />
            
            {/* Contenu de la popup */}
            <div style={{ padding: window.innerWidth <= 768 ? '16px' : '24px' }}>
              {/* Icône d'avertissement */}
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                marginBottom: window.innerWidth <= 768 ? '16px' : '20px'
              }}>
                <div style={{
                  width: window.innerWidth <= 768 ? '56px' : '64px',
                  height: window.innerWidth <= 768 ? '56px' : '64px',
                  borderRadius: '50%',
                  background: '#fef2f2',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '3px solid #fee2e2'
                }}>
                  <i className="fas fa-exclamation-triangle" style={{
                    fontSize: window.innerWidth <= 768 ? '28px' : '32px',
                    color: '#ef4444'
                  }} />
                </div>
              </div>

              {/* Titre */}
              <h2 style={{
                margin: 0,
                fontSize: window.innerWidth <= 768 ? '18px' : '20px',
                fontWeight: 700,
                color: '#1f2937',
                textAlign: 'center',
                marginBottom: '12px'
              }}>
                Delete Comment
              </h2>

              {/* Message */}
              <p style={{
                margin: 0,
                fontSize: window.innerWidth <= 768 ? '13px' : '14px',
                color: '#6b7280',
                textAlign: 'center',
                lineHeight: '1.6',
                marginBottom: window.innerWidth <= 768 ? '20px' : '24px'
              }}>
                Are you sure you want to delete the comment from <strong>{commentToDelete.author}</strong> on <strong>"{commentToDelete.article}"</strong>? This action cannot be undone.
              </p>

              {/* Boutons */}
              <div style={{
                display: 'flex',
                gap: window.innerWidth <= 768 ? '8px' : '12px',
                flexDirection: window.innerWidth <= 768 ? 'column' : 'row'
              }}>
                <button
                  onClick={() => setCommentToDelete(null)}
                  style={{
                    flex: 1,
                    padding: '12px 20px',
                    background: '#f3f4f6',
                    color: '#374151',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 600,
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = '#e5e7eb';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = '#f3f4f6';
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteComment}
                  style={{
                    flex: 1,
                    padding: '12px 20px',
                    background: '#ef4444',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 600,
                    transition: 'all 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = '#dc2626';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = '#ef4444';
                  }}
                >
                  <i className="fas fa-trash" />
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Popup de confirmation de rejet */}
      {commentToReject && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10001,
            padding: window.innerWidth <= 768 ? '10px' : '20px',
            paddingTop: window.innerWidth <= 768 ? '74px' : '20px',
            overflowY: 'auto'
          }}
        >
          <div
            style={{
              background: 'white',
              borderRadius: window.innerWidth <= 768 ? '12px' : '16px',
              maxWidth: window.innerWidth <= 768 ? '100%' : '450px',
              width: '100%',
              position: 'relative',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Barre de gradient */}
            <div style={{
              position: 'absolute',
              top: '0px',
              left: '0px',
              right: '0px',
              height: '4px',
              background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
              borderRadius: window.innerWidth <= 768 ? '12px 12px 0 0' : '16px 16px 0 0'
            }} />
            
            {/* Contenu de la popup */}
            <div style={{ padding: window.innerWidth <= 768 ? '16px' : '24px' }}>
              {/* Icône d'avertissement */}
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                marginBottom: window.innerWidth <= 768 ? '16px' : '20px'
              }}>
                <div style={{
                  width: window.innerWidth <= 768 ? '56px' : '64px',
                  height: window.innerWidth <= 768 ? '56px' : '64px',
                  borderRadius: '50%',
                  background: '#fef3c7',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '3px solid #fde68a'
                }}>
                  <i className="fas fa-exclamation-triangle" style={{
                    fontSize: window.innerWidth <= 768 ? '28px' : '32px',
                    color: '#f59e0b'
                  }} />
                </div>
              </div>

              {/* Titre */}
              <h2 style={{
                margin: 0,
                fontSize: window.innerWidth <= 768 ? '18px' : '20px',
                fontWeight: 700,
                color: '#1f2937',
                textAlign: 'center',
                marginBottom: '12px'
              }}>
                Reject Comment
              </h2>

              {/* Message */}
              <p style={{
                margin: 0,
                fontSize: window.innerWidth <= 768 ? '13px' : '14px',
                color: '#6b7280',
                textAlign: 'center',
                lineHeight: '1.6',
                marginBottom: window.innerWidth <= 768 ? '20px' : '24px'
              }}>
                Are you sure you want to reject the comment from <strong>{commentToReject.author}</strong> on <strong>"{commentToReject.article}"</strong>? It will no longer be publicly visible.
              </p>

              {/* Boutons */}
              <div style={{
                display: 'flex',
                gap: window.innerWidth <= 768 ? '8px' : '12px',
                flexDirection: window.innerWidth <= 768 ? 'column' : 'row'
              }}>
                <button
                  onClick={() => setCommentToReject(null)}
                  style={{
                    flex: 1,
                    padding: '12px 20px',
                    background: '#f3f4f6',
                    color: '#374151',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 600,
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = '#e5e7eb';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = '#f3f4f6';
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmRejectComment}
                  style={{
                    flex: 1,
                    padding: '12px 20px',
                    background: '#f59e0b',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 600,
                    transition: 'all 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = '#d97706';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = '#f59e0b';
                  }}
                >
                  <i className="fas fa-times" />
                  Reject
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Popup de confirmation de rejet de réponse */}
      {replyToReject && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10001,
            padding: window.innerWidth <= 768 ? '10px' : '20px',
            paddingTop: window.innerWidth <= 768 ? '74px' : '20px',
            overflowY: 'auto'
          }}
        >
          <div
            style={{
              background: 'white',
              borderRadius: window.innerWidth <= 768 ? '12px' : '16px',
              maxWidth: window.innerWidth <= 768 ? '100%' : '450px',
              width: '100%',
              position: 'relative',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Barre de gradient */}
            <div style={{
              position: 'absolute',
              top: '0px',
              left: '0px',
              right: '0px',
              height: '4px',
              background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
              borderRadius: window.innerWidth <= 768 ? '12px 12px 0 0' : '16px 16px 0 0'
            }} />
            
            {/* Contenu de la popup */}
            <div style={{ padding: window.innerWidth <= 768 ? '16px' : '24px' }}>
              {/* Icône d'avertissement */}
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                marginBottom: window.innerWidth <= 768 ? '16px' : '20px'
              }}>
                <div style={{
                  width: window.innerWidth <= 768 ? '56px' : '64px',
                  height: window.innerWidth <= 768 ? '56px' : '64px',
                  borderRadius: '50%',
                  background: '#fef3c7',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '3px solid #fde68a'
                }}>
                  <i className="fas fa-exclamation-triangle" style={{
                    fontSize: window.innerWidth <= 768 ? '28px' : '32px',
                    color: '#f59e0b'
                  }} />
                </div>
              </div>

              {/* Titre */}
              <h2 style={{
                margin: 0,
                fontSize: window.innerWidth <= 768 ? '18px' : '20px',
                fontWeight: 700,
                color: '#1f2937',
                textAlign: 'center',
                marginBottom: '12px'
              }}>
                Reject Reply
              </h2>

              {/* Message */}
              <p style={{
                margin: 0,
                fontSize: window.innerWidth <= 768 ? '13px' : '14px',
                color: '#6b7280',
                textAlign: 'center',
                lineHeight: '1.6',
                marginBottom: window.innerWidth <= 768 ? '20px' : '24px'
              }}>
                Are you sure you want to reject the reply from <strong>{replyToReject.author}</strong> on <strong>"{replyToReject.article}"</strong>? It will no longer be publicly visible.
              </p>

              {/* Boutons */}
              <div style={{
                display: 'flex',
                gap: window.innerWidth <= 768 ? '8px' : '12px',
                flexDirection: window.innerWidth <= 768 ? 'column' : 'row'
              }}>
                <button
                  onClick={() => setReplyToReject(null)}
                  style={{
                    flex: 1,
                    padding: '12px 20px',
                    background: '#f3f4f6',
                    color: '#374151',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 600,
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = '#e5e7eb';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = '#f3f4f6';
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmRejectReply}
                  style={{
                    flex: 1,
                    padding: '12px 20px',
                    background: '#f59e0b',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 600,
                    transition: 'all 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = '#d97706';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = '#f59e0b';
                  }}
                >
                  <i className="fas fa-times" style={{ marginRight: '4px' }} />
                  Reject
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default Comments;
